import { LANGUAGES } from '@/content/languages';
import { APP_ROUTES_DEFAULT, AppRoutes, RoutesListType } from './appRoutes';

export type WsoAuthType = 'oidc' | 'kerberos';

interface WSO {
    AUTH_TYPE?: WsoAuthType; // 'oidc' for WSO2, 'kerberos' for domain-idp-service. Defaults to 'oidc'
    CLIENT_SECRET: string;
    CLIENT_ID: string;
    DOMAIN: string;
    REDIRECT_URL: string;
}

export interface API_PROXY {
    CHECK_APP_INFO_BEFORE_USING: boolean;
    API_URL: string;
    APP_URL: string;
    DISABLE_MFA_BEHIND_PROXY: boolean;
    DISABLE_OTP_BEHIND_PROXY: boolean;
}

export enum SSOTypes {
    cognito = 'cognito',
    google = 'google',
    microsoft = 'microsoft',
    okta = 'okta',
}

interface Cognito {
    USER_POOL_ID: string;
    APP_CLIENT_ID: string;
    IDENTITY_POOL_ID: string;
    DOMAIN: string;
    IDP_CUSTOM_HOST?: string;
    IDENTITY_CUSTOM_HOST?: string;
}

const getCognitoRegion = (identityPoolId: string): string => identityPoolId.split(':')[0];

const defaultCognitoIdpHost = (region: string): string => `cognito-idp.${region}.amazonaws.com`;

const defaultCognitoIdentityHost = (region: string): string => `cognito-identity.${region}.amazonaws.com`;

export interface ViewerConfig {
    CANCEL_MOBILE_VIEWER_FILE_EXTENSION_CHECK?: boolean;
    HIDE_DOWNLOAD_BUTTON?: boolean;
    ENABLE_SCREENSHOT_DETERRENCE?: boolean;
}

/** Connector service URLs for health checks */
export interface ConnectorUrls {
    slack?: string;
    salesforce?: string;
}

export interface EnvConfig {
    COMPANY_LOGO: string;
    HEADER_COMPANY_LOGO: string;
    HEADER_SMALL_COMPANY_LOGO: string;
    API_URL: string;
    APP_URL: string;
    cognito: Cognito;
    WSO?: WSO;
    API_PROXY?: API_PROXY;
    SSO: SSOTypes[];
    DEFAULT_LANGUAGE?: LANGUAGES;
    HAS_FULL_OTP_AUTH?: boolean;
    SHOW_FEDERATED_POPUP?: boolean;
    DISABLE_GOOGLE_REGISTER_CHECK?: boolean;
    DISABLE_POLICY_CHANGES?: boolean;
    DISABLE_POLICY_CHANGE_FOR_NESTED_ITEMS?: boolean;
    HIDE_SHARED_FILE_POLICIES?: boolean;
    HIDE_UPLOAD_BUTTON?: boolean;
    DISALLOW_SHARE_WHILE_FILE_UPLOADING?: boolean;
    DISABLE_CREATE_FOLDER?: boolean;
    viewer?: ViewerConfig;
    PDF_VIEWER_ENGINE?: 'pspdfkit' | 'pdf-viewer';
    HIDE_FAQ_BUTTON?: boolean;
    ROUTES?: RoutesListType;
    ENABLE_WORKSPACE?: boolean;
    ENABLE_RIGHTS_MANAGEMENT_POLICY_SETTING?: boolean;
    ENABLE_IP_RESTRICTION?: boolean;
    SHOW_UPLOAD_DISCLAIMER?: boolean;
    DISABLE_SELF_PHONE_FILLING?: boolean;
    ENABLE_OPSWAT_FILE_SCANNING?: boolean;
    UPLOAD_FILES_COUNT_LIMIT?: number;
    MAX_UPLOAD_FILE_SIZE_MB?: number;
    SHOW_SHARE_BACK_BUTTON?: boolean;
    ENABLE_SHARE_MESSAGE?: boolean;
    IS_MULTITENANCY?: boolean;
    DEFAULT_SHARE_ROLE?: 'viewer' | 'editor' | 'coOwner';
    ROLE_FILTER_DEFAULT?: string[];
    OTP_ALLOWED_IPS?: string[];
    PASSWORD_DEFAULT_HINT?: string;
    USE_SIMPLIFIED_PASSWORD_PROMPT?: boolean;
    CONNECTORS?: ('slack' | 'salesforce' | 'purview' | 'sharepoint_connector')[];
    CONNECTOR_URLS?: ConnectorUrls;
    /**
     * Controls OTP sending behavior on the recipient page.
     * - true (default): OTP is sent automatically on page load
     * - false: User must click "Send Code" button before OTP is sent
     */
    AUTOMATIC_OTP?: boolean;
    DEFAULT_NOTIFY_RECIPIENTS?: boolean;
    /** When true, redirect to the login page instead of showing the "phone missing" modal */
    LOGIN_FALLBACK_ON_MISSING_PHONE?: boolean;
    /** Hides the OTP "Login with Code" entry points (header button and reset-password link). demo3 / migdal. */
    HIDE_LOGIN_WITH_CODE?: boolean;
    HIDE_OTP_ON_RESET_PASSWORD?: boolean;
    /**
     * When true, honor auth-flow `requireStandardLogin` (SPX-6391): frequent-group
     * recipients auto-redirect to Cognito /signIn and skip recipient OTP.
     * Off on staging so QA keeps Login with Code / OTP; on for migdal / demo3.
     */
    ENABLE_FREQUENT_STANDARD_LOGIN?: boolean;
    /** Hides the standard Login button on the recipient page. demo3 / migdal. */
    HIDE_RECIPIENT_LOGIN_BUTTON?: boolean;
    /**
     * Customer helpdesk/support email(s) for this tenant. Used as the recipient(s) for
     * user-reported errors (e.g. the "Report a problem" flow on the load-error screen).
     * Distinct from the SpecterX provider address `SPX_SUPPORT_EMAIL`; when unset the
     * resolver falls back to `[SPX_SUPPORT_EMAIL]`. Multiple addresses are supported.
     */
    HELPDESK_EMAILS?: string[];
    /**
     * Branding profile (SPX-6494), aligned with the platform-wide
     * `BANNER_BRANDING=specterx|unbranded` convention (SPX-6532). With
     * `unbranded`, every SpecterX logo and user-visible "SpecterX" text is
     * neutralized across the app and the recipient link experience; tenant
     * logos (COMPANY_LOGO / HEADER_*) are still shown. Unset falls back to
     * `specterx` (backward compatible). Union mirrors the `Branding` type in
     * `config/branding.ts` (kept inline to avoid an import cycle); can also
     * be overridden via the VITE_BRANDING env var.
     */
    BRANDING?: 'specterx' | 'unbranded';
}

const bwell: EnvConfig = {
    COMPANY_LOGO: '/assets/company_logo/bwell.png',
    HEADER_COMPANY_LOGO: '/assets/header_company_logo/bwell.png',
    HEADER_SMALL_COMPANY_LOGO: '/assets/header_company_logo/small_logos/bwell-small.png',
    API_URL: 'https://bwell-api.specterx.com',
    APP_URL: 'https://bwell.specterx.com',
    cognito: {
        USER_POOL_ID: 'eu-central-1_i40R4ZI4n',
        APP_CLIENT_ID: '7hb5s9e9g7q1oqhrg8oa94ljv',
        IDENTITY_POOL_ID: 'eu-central-1:4e0c2de4-4ff8-4113-9886-9dbb931c461c',
        DOMAIN: 'bwell-users.auth.eu-central-1.amazoncognito.com',
    },
    SSO: [SSOTypes.cognito, SSOTypes.microsoft],
    ENABLE_IP_RESTRICTION: false,
    UPLOAD_FILES_COUNT_LIMIT: 50,
    SHOW_SHARE_BACK_BUTTON: false,
    DISABLE_GOOGLE_REGISTER_CHECK: false,
    PASSWORD_DEFAULT_HINT: 'Your Social ID number',
};

let config: EnvConfig;
config = bwell;
const appConfig: Readonly<EnvConfig> = {
    // Add common config values here

    DEFAULT_LANGUAGE: LANGUAGES.en,
    HAS_FULL_OTP_AUTH: true,
    SHOW_FEDERATED_POPUP: false,
    DISABLE_GOOGLE_REGISTER_CHECK: true,
    DISABLE_CREATE_FOLDER: false,
    HIDE_SHARED_FILE_POLICIES: false,
    DISALLOW_SHARE_WHILE_FILE_UPLOADING: false,
    HIDE_FAQ_BUTTON: true,
    HIDE_UPLOAD_BUTTON: false,
    ENABLE_WORKSPACE: true,
    ENABLE_RIGHTS_MANAGEMENT_POLICY_SETTING: true,
    ROUTES: APP_ROUTES_DEFAULT,
    DISABLE_SELF_PHONE_FILLING: true,
    SHOW_SHARE_BACK_BUTTON: true,
    IS_MULTITENANCY: false,
    ENABLE_SHARE_MESSAGE: true,
    PASSWORD_DEFAULT_HINT: 'Your Social ID number',
    ROLE_FILTER_DEFAULT: ['Administrators', 'Collaborators', 'Auditors'],
    DEFAULT_NOTIFY_RECIPIENTS: true,
    USE_SIMPLIFIED_PASSWORD_PROMPT: true,
    PDF_VIEWER_ENGINE: 'pspdfkit',
    ...config,
    cognito: {
        IDP_CUSTOM_HOST: defaultCognitoIdpHost(getCognitoRegion(config.cognito.IDENTITY_POOL_ID)),
        IDENTITY_CUSTOM_HOST: defaultCognitoIdentityHost(getCognitoRegion(config.cognito.IDENTITY_POOL_ID)),
        ...config.cognito,
    },
    // Ensure WSO has default AUTH_TYPE if WSO config exists
    ...(config.WSO && {
        WSO: {
            AUTH_TYPE: 'oidc' as const, // Default to OIDC (WSO2)
            ...config.WSO,
        },
    }),
};

export const IS_WSO = 'WSO' in config;
export const HAS_API_PROXY = 'API_PROXY' in config;

export const IS_LOCAL_ENV = import.meta.env.DEV;
export const IS_NON_PROD_ENV = IS_LOCAL_ENV || ['staging', 'dev'].includes(import.meta.env.VITE_STAGE);
export const IS_UNSTABLE_ENV = IS_LOCAL_ENV || import.meta.env.VITE_STAGE === 'dev';

export const IS_ONLY_SSO = appConfig.SSO.length === 1;
export const ALL_SSO: SSOTypes[] = Object.values(SSOTypes);
export const SSO_ORDER: Partial<Record<SSOTypes, number>> = {
    [SSOTypes.microsoft]: 1,
    [SSOTypes.okta]: 2,
    [SSOTypes.google]: 0,
};

Object.freeze(appConfig);

export default appConfig;
